import apiService from './api';
import type { ApiResponse } from '../types';

export interface PublicHoliday {
  id: number;
  organisation_id: number;
  date: string;
  name: string;
  is_national: boolean;
  state: string | null;
  is_custom: boolean;
  created_at: string;
}

export interface AddHolidayRequest {
  date: string;
  name: string;
  is_national?: boolean;
  state?: string | null;
  /** Who it applies to. Empty or omitted is everyone; each state gets its own row. */
  states?: string[];
}

export interface UpdateHolidayRequest {
  date: string;
  name: string;
  /** Who it applies to. Empty is everyone in the organisation. */
  states: string[];
}

export interface SeedRequest {
  /** Omit or leave empty to seed national + ALL states (recommended). */
  state?: string;
  year?: number;
}

class PublicHolidayService {
  async getHolidays(params: { year?: number; start_date?: string; end_date?: string } = {}): Promise<ApiResponse<PublicHoliday[]>> {
    const searchParams = new URLSearchParams();
    if (params.year) searchParams.set('year', String(params.year));
    if (params.start_date) searchParams.set('start_date', params.start_date);
    if (params.end_date) searchParams.set('end_date', params.end_date);
    const qs = searchParams.toString();
    return apiService.get(`/public-holidays${qs ? `?${qs}` : ''}`);
  }

  async checkDate(date: string): Promise<ApiResponse<{ is_holiday: boolean; holiday: PublicHoliday | null }>> {
    return apiService.get(`/public-holidays/check?date=${date}`);
  }

  async getStates(): Promise<ApiResponse<string[]>> {
    return apiService.get('/public-holidays/states');
  }

  async addHoliday(data: AddHolidayRequest): Promise<ApiResponse<PublicHoliday>> {
    return apiService.post('/public-holidays', data);
  }

  async updateHoliday(id: number, data: UpdateHolidayRequest): Promise<ApiResponse<PublicHoliday>> {
    return apiService.put(`/public-holidays/${id}`, data);
  }

  async seedHolidays(data: SeedRequest): Promise<ApiResponse<{ inserted: number }>> {
    return apiService.post('/public-holidays/seed', data);
  }

  async removeHoliday(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/public-holidays/${id}`);
  }
}

export const publicHolidayService = new PublicHolidayService();
export default publicHolidayService;
